/**
 * Form validation utilities that work with react-hook-form
 */

import { sanitizeText, sanitizeUrl } from './sanitization';

/**
 * Validation result type
 */
type ValidationResult = true | string;

/**
 * Create a validation rule for minimum length
 * @param min - Minimum length
 * @param message - Error message
 */
export function minLength(min: number, message?: string) {
  return {
    value: min,
    message: message || `Must be at least ${min} characters`,
  };
}

/**
 * Create a validation rule for maximum length
 * @param max - Maximum length
 * @param message - Error message
 */
export function maxLength(max: number, message?: string) {
  return {
    value: max,
    message: message || `Must be less than ${max} characters`,
  };
}

/**
 * Validate that input doesn't contain malicious content
 * @param value - Value to validate
 * @returns Validation result
 */
export function validateSafeContent(value: string): ValidationResult {
  const sanitized = sanitizeText(value);
  if (sanitized !== value) {
    return 'Input contains invalid characters';
  }
  return true;
}

/**
 * Validate URL format and safety
 * @param value - URL to validate
 * @returns Validation result
 */
export function validateSafeUrl(value: string): ValidationResult {
  try {
    const url = new URL(value);
    const sanitized = sanitizeUrl(value);
    
    if (!sanitized) {
      return 'Invalid or unsafe URL';
    }
    
    if (!['http:', 'https:'].includes(url.protocol)) {
      return 'URL must use HTTP or HTTPS protocol';
    }
    
    return true;
  } catch {
    return 'Please enter a valid URL';
  }
}

/**
 * Validate email format
 * @param value - Email to validate
 * @returns Validation result
 */
export function validateEmail(value: string): ValidationResult {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(value)) {
    return 'Please enter a valid email address';
  }
  return true;
}

/**
 * Validate that an array has items within bounds
 * @param min - Minimum items
 * @param max - Maximum items
 * @returns Validator function
 */
export function validateArrayLength(min: number, max: number) {
  return (value: unknown[]): ValidationResult => {
    if (value.length < min) {
      return `At least ${min} item${min === 1 ? '' : 's'} required`;
    }
    if (value.length > max) {
      return `Maximum ${max} item${max === 1 ? '' : 's'} allowed`;
    }
    return true;
  };
}

/**
 * Validate comma-separated tags
 * @param value - Tags string
 * @param min - Minimum tags
 * @param max - Maximum tags
 * @returns Validation result
 */
export function validateTags(value: string, min: number, max: number): ValidationResult {
  const tags = value
    .split(',')
    .map(tag => tag.trim())
    .filter(tag => tag.length > 0);
  
  if (tags.length < min) {
    return `At least ${min} tag${min === 1 ? '' : 's'} required`;
  }
  
  if (tags.length > max) {
    return `Maximum ${max} tag${max === 1 ? '' : 's'} allowed`;
  }
  
  // Check each tag for invalid content
  for (const tag of tags) {
    const sanitized = sanitizeText(tag);
    if (sanitized !== tag) {
      return `Tag "${tag}" contains invalid characters`;
    }
  }
  
  return true;
}

/**
 * Validate multiline capabilities or features
 * @param value - Multiline string
 * @param min - Minimum items
 * @param max - Maximum items
 * @returns Validation result
 */
export function validateMultilineItems(value: string, min: number, max: number): ValidationResult {
  const items = value
    .split('\n')
    .map(item => item.trim())
    .filter(item => item.length > 0);
  
  if (items.length < min) {
    return `At least ${min} item${min === 1 ? '' : 's'} required`;
  }
  
  if (items.length > max) {
    return `Maximum ${max} item${max === 1 ? '' : 's'} allowed`;
  }
  
  return true;
}

/**
 * Composite validator that runs multiple validations
 * @param validators - Array of validator functions
 * @returns Validator function
 */
export function composeValidators(...validators: ((value: unknown) => ValidationResult)[]) {
  return (value: unknown): ValidationResult => {
    for (const validator of validators) {
      const result = validator(value);
      if (result !== true) {
        return result;
      }
    }
    return true;
  };
}

/**
 * Create a custom validation message for required fields
 * @param fieldName - Name of the field
 * @returns Error message
 */
export function requiredMessage(fieldName: string): string {
  return `${fieldName} is required`;
}

/**
 * Validate that a value is one of allowed options
 * @param allowedValues - Array of allowed values
 * @param message - Error message
 * @returns Validator function
 */
export function oneOf<T>(allowedValues: readonly T[], message?: string) {
  return (value: T): ValidationResult => {
    if (!allowedValues.includes(value)) {
      return message || `Must be one of: ${allowedValues.join(', ')}`;
    }
    return true;
  };
}